import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { testimonialsApi } from '../../api/testimonials';
import { Star, Quote, Plus, X, CheckCircle2, Send } from 'lucide-react';
import './Testimonials.css';

export function Testimonials() {
    const { t, language } = useLanguage();
    const isRtl = language === 'ar';
    const L = (ar, en) => isRtl ? ar : en;

    const [testimonialsData, setTestimonialsData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [formData, setFormData] = useState({ name: '', role: '', institution: '', message: '', rating: 5 });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const load = async () => {
            try {
                const data = await testimonialsApi.getAll();
                if (data) setTestimonialsData(data.filter(t => t.isActive).sort((a,b) => a.order - b.order));
            } catch (err) { console.error(err); }
            finally { setIsLoading(false); }
        };
        load();
    }, []);

    const validateForm = () => {
        const newErrors = {};
        const nameVal = formData.name.trim();
        const roleVal = formData.role.trim();
        const instVal = formData.institution.trim();
        const msgVal = formData.message.trim();

        if (nameVal.length < 3) newErrors.name = L('أدخل 3 أحرف على الأقل', 'Min 3 chars');
        else if (!/^[\p{L}\s]+$/u.test(nameVal)) newErrors.name = L('حروف فقط', 'Letters only');

        if (roleVal && roleVal.length < 2) newErrors.role = L('حرفين على الأقل', 'Min 2 chars');
        else if (roleVal && !/^[\p{L}\s]+$/u.test(roleVal)) newErrors.role = L('حروف فقط', 'Letters only');

        if (instVal && instVal.length < 2) newErrors.institution = L('حرفين على الأقل', 'Min 2 chars');
        else if (instVal && !/^[\p{L}\p{N}\s]+$/u.test(instVal)) newErrors.institution = L('حروف وأرقام فقط', 'Letters and numbers only');

        if (msgVal.length < 10) newErrors.message = L('اكتب 10 أحرف على الأقل', 'Min 10 chars');
        else if (/([^\p{L}\p{N}\s])\1{3,}/u.test(msgVal)) newErrors.message = L('تجنب تكرار الرموز', 'Avoid repetitive symbols');

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);
        try {
            await testimonialsApi.create({ 
                clientName: formData.name.trim(), 
                clientRole: formData.role.trim() || L('عميل', 'Client'), 
                institutionName: formData.institution.trim(), 
                message: formData.message.trim(), 
                rating: formData.rating, 
                isActive: false 
            });
            setIsSuccess(true);
            setTimeout(() => { setIsModalOpen(false); setIsSuccess(false); setFormData({ name: '', role: '', institution: '', message: '', rating: 5 }); setErrors({}); }, 3000);
        } catch (err) { alert(L('الخطأ: ', 'Error: ') + (err.response?.data?.message || err.response?.data?.title || err.message || 'Unknown Backend Error')); console.error('API REJECTION:', err.response || err); }
        finally { setIsSubmitting(false); }
    };

    if (isLoading) return null;

    return (
        <section className="home-testimonials-section" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="container">
                <div className="section-header centered">
                    <h2>{isRtl ? 'قصص نجاح عملائنا' : 'Success Stories'}</h2>
                    <button onClick={() => {setIsModalOpen(true); setErrors({});}} style={{ marginTop: '20px', padding: '14px 32px', backgroundColor: '#d4af37', color: '#fff', borderRadius: '30px', border: 'none', fontWeight: '800', cursor: 'pointer', position: 'relative', zIndex: 10, display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <Plus size={18} /> {isRtl ? 'أضف رأيك' : 'Add Review'}
                    </button>
                </div>
                <div className="testimonials-grid">
                    {testimonialsData.map((tst, idx) => (
                        <div key={tst.id || idx} className="tst-card-premium">
                            <div className="tst-card-header">
                                <Quote size={30} strokeWidth={1} />
                                <div className="stars-row">{[...Array(tst.rating || 5)].map((_, i) => <Star key={i} size={14} fill="#F5A623" stroke="none" />)}</div>
                            </div>
                            <p className="tst-text">{tst.message}</p>
                            <div className="tst-footer">
                                <div className="tst-initial" style={{ background: '#d4af37' }}>{(tst.clientName || 'A').charAt(0)}</div>
                                <div className="tst-info">
                                    <h4 className="tst-name">{tst.clientName}</h4>
                                    <p className="tst-role">{tst.institutionName ? `${tst.clientRole} @ ${tst.institutionName}` : tst.clientRole}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {isModalOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)', zIndex: 999999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '15px' }}>
                    <div style={{ backgroundColor: '#fff', borderRadius: '25px', padding: '35px 25px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', position: 'relative', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
                        <button onClick={() => setIsModalOpen(false)} style={{ position: 'absolute', top: '15px', [isRtl?'left':'right']: '15px', background: '#f1f5f9', border: 'none', width: '35px', height: '35px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={18} color="#64748b" /></button>
                        
                        {isSuccess ? (
                            <div style={{ textAlign: 'center', padding: '30px' }}><CheckCircle2 size={60} color="#d4af37" style={{margin:'0 auto 15px'}} /><h3>{isRtl?'تم الإرسال!':'Sent!'}</h3><p style={{color:'#666', fontSize:'14px'}}>{isRtl?'بانتظار المراجعة':'Pending approval'}</p></div>
                        ) : (
                            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <h3 style={{ textAlign: 'center', marginBottom: '5px', fontSize: '22px', fontWeight: '800' }}>{isRtl ? 'شاركنا رأيك' : 'Rate Us'}</h3>
                                
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: '#334155' }}>{isRtl ? 'الاسم' : 'Name'} <span style={{color:'#ef4444'}}>*</span></label>
                                    <input placeholder={isRtl?'أحمد محمود':'John Doe'} style={{ width:'100%', padding: '14px', borderRadius: '12px', border: errors.name?'2px solid #ef4444':'1.5px solid #e2e8f0', outline: 'none', fontSize:'15px', backgroundColor: '#f8fafc' }} value={formData.name} onChange={e=>{ setFormData({...formData, name: e.target.value}); if(errors.name) setErrors({...errors, name:null}); }} />
                                    {errors.name && <span style={{color:'#ef4444', fontSize:'13px', marginTop:'6px', display:'block', fontWeight: '600'}}>{errors.name}</span>}
                                </div>

                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <div style={{flex: 1}}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: '#334155' }}>{isRtl ? 'المنصب' : 'Role'}</label>
                                        <input placeholder={isRtl?'مدير، مهندس...':'Manager...'} style={{ width:'100%', padding: '14px', borderRadius: '12px', border: errors.role?'2px solid #ef4444':'1.5px solid #e2e8f0', outline: 'none', fontSize:'15px', backgroundColor: '#f8fafc' }} value={formData.role} onChange={e=>{ setFormData({...formData, role: e.target.value}); if(errors.role) setErrors({...errors, role:null}); }} />
                                        {errors.role && <span style={{color:'#ef4444', fontSize:'13px', marginTop:'6px', display:'block', fontWeight: '600'}}>{errors.role}</span>}
                                    </div>
                                    <div style={{flex: 1}}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: '#334155' }}>{isRtl ? 'الجهة' : 'Company'}</label>
                                        <input placeholder={isRtl?'اسم شركتك':'Company Name'} style={{ width:'100%', padding: '14px', borderRadius: '12px', border: errors.institution?'2px solid #ef4444':'1.5px solid #e2e8f0', outline: 'none', fontSize:'15px', backgroundColor: '#f8fafc' }} value={formData.institution} onChange={e=>{ setFormData({...formData, institution: e.target.value}); if(errors.institution) setErrors({...errors, institution:null}); }} />
                                        {errors.institution && <span style={{color:'#ef4444', fontSize:'13px', marginTop:'6px', display:'block', fontWeight: '600'}}>{errors.institution}</span>}
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: '#334155' }}>{isRtl ? 'التقييم' : 'Rating'} <span style={{color:'#ef4444'}}>*</span></label>
                                    <div style={{ display: 'flex', gap: '8px', direction: 'ltr', justifyContent: isRtl ? 'flex-end' : 'flex-start' }}>
                                        {[1,2,3,4,5].map(s => <Star key={s} size={32} fill={s<=formData.rating?'#d4af37':'none'} stroke={s<=formData.rating?'#d4af37':'#cbd5e1'} onClick={()=>setFormData({...formData, rating: s})} style={{cursor:'pointer', transition:'0.2s'}} />)}
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '700', color: '#334155' }}>{isRtl ? 'رسالتك' : 'Message'} <span style={{color:'#ef4444'}}>*</span></label>
                                    <textarea placeholder={isRtl?'كيف كانت تجربتك معنا؟':'How was your experience?'} rows="4" style={{ width:'100%', padding: '14px', borderRadius: '12px', border: errors.message?'2px solid #ef4444':'1.5px solid #e2e8f0', outline: 'none', resize:'none', fontSize:'15px', backgroundColor: '#f8fafc' }} value={formData.message} onChange={e=>{ setFormData({...formData, message: e.target.value}); if(errors.message) setErrors({...errors, message:null}); }} />
                                    {errors.message && <span style={{color:'#ef4444', fontSize:'13px', marginTop:'6px', display:'block', fontWeight: '600'}}>{errors.message}</span>}
                                </div>

                                <button type="submit" disabled={isSubmitting} style={{ width: '100%', padding: '18px', background: 'linear-gradient(135deg, #111 0%, #222 100%)', color: '#fff', border: 'none', borderRadius: '12px', fontWeight: '800', fontSize:'16px', cursor: 'pointer', marginTop: '10px', transition:'0.3s', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                                    {isSubmitting ? '...' : L('إرسال التقييم', 'Submit Review')} {!isSubmitting && <Send size={20} style={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}
